// The WizzGift catalog as users see it on the cash-out page: balance and sync health up top, then
// every card with its price inputs. Per-card markup and position save when the box loses focus.
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Gift, RefreshCw, X } from 'lucide-react';
import { api, ago, usd } from '../lib/api';
import { useCan } from '../lib/staff';
import {
  EmptyState,
  ErrorNote,
  PageHeader,
  ScrollX,
  SearchInput,
  SkeletonRows,
  toast,
} from '../components/ui';

interface Row {
  id: string;
  name: string;
  imageUrl: string | null;
  currency: string | null;
  costCurrency: string | null;
  countries: string[];
  available: boolean;
  enabled: boolean;
  needsFields: boolean;
  /** Labels of what the user fills in when ordering. */
  fields: string[];
  markupPercent: number | null;
  sort: number | null;
  skus: { min: number; max: number; minCost: number; minCostUsd: number }[];
  syncedAt: number;
}
interface Data {
  products: Row[];
  matched: number;
  offset: number;
  total: number;
  offered: number;
  sync: {
    at: number;
    ok: boolean;
    products: number;
    skipped: Record<string, number>;
    error: string | null;
    fxAt?: number | null;
    fxError?: string | null;
    changed?: number;
    removed?: number;
  } | null;
  syncRunningSince: number | null;
  /** written/total stay null while the catalog is still being fetched. */
  syncProgress: { since: number; written: number | null; total: number | null } | null;
  balance: { balance: number; currency: string } | null;
  balanceError: string | null;
  markupPercent: number;
  fxBufferPercent: number;
}

const PAGE = 100;
const fmt = (n: number) => n.toLocaleString('en-US');

function amounts(r: Row): string {
  const lo = Math.min(...r.skus.map((s) => s.min));
  const hi = Math.max(...r.skus.map((s) => s.max));
  const cur = r.currency ?? '';
  return lo === hi ? `${fmt(lo)} ${cur}` : `${fmt(lo)}–${fmt(hi)} ${cur}`;
}

/** Group skip reasons: "no exchange rate for X" collapses into one line listing currencies. */
function skipSummary(skipped: Record<string, number>) {
  const fx: [string, number][] = [];
  const other: [string, number][] = [];
  for (const [reason, n] of Object.entries(skipped)) {
    const m = /^(?:no exchange rate for|cost in) (\w+)$/.exec(reason);
    if (m) fx.push([m[1]!, n]);
    else other.push([reason, n]);
  }
  fx.sort((a, b) => b[1] - a[1]);
  const total = Object.values(skipped).reduce((t, n) => t + n, 0);
  // "cost in X" is what syncs wrote before currency conversion existed.
  const legacy = Object.keys(skipped).some((r) => r.startsWith('cost in '));
  return { fx, other, total, legacy };
}

export function GiftCards() {
  const qc = useQueryClient();
  // Syncing, pricing and switching cards on or off: giftcards.manage. Everyone else reads.
  const manage = useCan()('giftcards.manage');
  const [q, setQ] = useState('');
  const [country, setCountry] = useState('');
  const [offset, setOffset] = useState(0);
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['giftcards', q, country, offset],
    queryFn: () =>
      api<Data>(
        `/giftcards?q=${encodeURIComponent(q)}&country=${encodeURIComponent(country)}&offset=${offset}`,
      ),
    placeholderData: (prev) => prev,
    // While a queued sync runs, refresh until its result lands.
    refetchInterval: (query) => (query.state.data?.syncRunningSince ? 3000 : false),
  });
  const [syncing, setSyncing] = useState(false);
  const [showSkips, setShowSkips] = useState(false);
  const [markup, setMarkup] = useState('');
  const [buffer, setBuffer] = useState('');
  const [pricingErr, setPricingErr] = useState<string | null>(null);
  useEffect(() => {
    if (!data) return;
    setMarkup(String(data.markupPercent));
    setBuffer(String(data.fxBufferPercent));
  }, [data?.markupPercent, data?.fxBufferPercent]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => setOffset(0), [q, country]);

  const running = !!data?.syncRunningSince;
  const progress = data?.syncProgress;
  const runLabel =
    progress?.total != null
      ? `Writing ${fmt(Math.min(progress.written ?? 0, progress.total))} of ${fmt(progress.total)}`
      : 'Fetching catalog…';
  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api<{ state: 'queued' | 'running' }>('/giftcards/sync', { body: {} });
      toast(
        r.state === 'queued'
          ? 'Sync started. It runs in the background and usually takes a minute or two.'
          : 'A sync is already running.',
      );
      await qc.invalidateQueries({ queryKey: ['giftcards'] });
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setSyncing(false);
    }
  };
  const patch = async (id: string, body: Record<string, unknown>) => {
    try {
      await api(`/giftcards/${id}`, { method: 'PATCH', body });
      await qc.invalidateQueries({ queryKey: ['giftcards'] });
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };
  const savePricing = async () => {
    setPricingErr(null);
    const m = Number(markup);
    const b = Number(buffer);
    if (![m, b].every((v) => Number.isFinite(v) && v >= 0 && v <= 50))
      return setPricingErr('Both must be between 0 and 50%.');
    try {
      await api('/settings', {
        method: 'PUT',
        body: { key: 'giftcards.markupPercent', value: m },
      });
      await api('/settings', {
        method: 'PUT',
        body: { key: 'giftcards.fxBufferPercent', value: b },
      });
      toast(`Pricing saved: ${m}% markup, ${b}% currency buffer.`);
      await qc.invalidateQueries({ queryKey: ['giftcards'] });
    } catch (e) {
      setPricingErr((e as Error).message);
    }
  };
  const pricingDirty =
    !!data && (markup !== String(data.markupPercent) || buffer !== String(data.fxBufferPercent));

  const skips = data?.sync ? skipSummary(data.sync.skipped) : null;
  const failed = !!data?.sync && !data.sync.ok && !running;
  const filtered = !!(q || country);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Gift cards"
        description="The WizzGift catalog as users see it on the cash-out page. Prices update on every sync; switch a card off to hide it."
        actions={
          manage && (
            <button className="btn-primary" onClick={sync} disabled={syncing || running}>
              <RefreshCw
                size={16}
                aria-hidden="true"
                className={syncing || running ? 'animate-spin' : ''}
              />
              {running ? 'Sync running…' : syncing ? 'Starting…' : 'Sync now'}
            </button>
          )
        }
      />
      <ErrorNote error={error} onRetry={() => void refetch()} />

      {/* ---------- Summary ---------- */}
      {!(error && !data) && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Stat label="WizzGift balance" loading={!data}>
            <div className="text-[28px] font-extrabold leading-tight tracking-[-0.02em] tabular-nums text-coin-deep">
              {data?.balance ? usd(data.balance.balance) : '—'}
            </div>
            <p
              className={`mt-1 text-[12px] ${data?.balanceError ? 'text-berry-deep' : 'text-stone'}`}
            >
              {data?.balanceError ?? 'Pays for every card. Top up on wizzgift.com.'}
            </p>
          </Stat>
          <Stat label="Offered to users" loading={!data}>
            <div className="text-[28px] font-extrabold leading-tight tracking-[-0.02em] tabular-nums">
              {data ? fmt(data.offered) : '—'}
              <span className="text-[15px] font-semibold tracking-normal text-stone">
                {data ? ` of ${fmt(data.total)}` : ''}
              </span>
            </div>
            <p className="mt-1 text-[12px] text-stone">In stock and switched on</p>
          </Stat>
          <Stat label="Last sync" loading={!data}>
            {running ? (
              <>
                <div className="flex h-[35px] items-center text-[17px] font-extrabold tabular-nums">
                  {runLabel}
                </div>
                <div
                  className="mt-2 h-1.5 overflow-hidden rounded-full bg-ink/10"
                  role="progressbar"
                  aria-label="Sync progress"
                  aria-valuemin={0}
                  aria-valuemax={progress?.total ?? undefined}
                  aria-valuenow={progress?.written ?? undefined}
                >
                  <div
                    className={`h-full rounded-full bg-ink ${progress?.total ? '' : 'w-1/3 animate-pulse'}`}
                    style={
                      progress?.total
                        ? {
                            width: `${Math.max(4, Math.min(100, ((progress.written ?? 0) / progress.total) * 100))}%`,
                          }
                        : undefined
                    }
                  />
                </div>
              </>
            ) : (
              <div
                className={`text-[28px] font-extrabold leading-tight tracking-[-0.02em] ${failed ? 'text-berry-deep' : ''}`}
              >
                {data?.sync ? `${ago(data.sync.at)} ago` : 'Never'}
              </div>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-x-1.5 text-[12px] text-stone">
              {data?.sync?.error ? (
                <span className="font-semibold text-berry-deep">{data.sync.error}</span>
              ) : skips && skips.total > 0 ? (
                <>
                  {data?.sync?.changed !== undefined && (
                    <span>{fmt(data.sync.changed)} updated ·</span>
                  )}
                  <span>{fmt(skips.total)} not imported ·</span>
                  <button
                    className="-my-1 rounded-[6px] px-1 py-1 font-semibold text-ink underline max-sm:min-h-9 decoration-ink/30 underline-offset-2 transition hover:decoration-ink"
                    aria-expanded={showSkips}
                    onClick={() => setShowSkips((s) => !s)}
                  >
                    {showSkips ? 'Hide reasons' : 'Why?'}
                  </button>
                </>
              ) : (
                <span>Runs on its own every 3 hours</span>
              )}
            </div>
          </Stat>
          <Stat label="Default pricing" loading={!data}>
            <div className="grid grid-cols-2 gap-2">
              <PercentField
                id="gc-markup"
                label="Markup"
                value={markup}
                onChange={setMarkup}
                disabled={!manage}
              />
              <PercentField
                id="gc-buffer"
                label="FX buffer"
                value={buffer}
                onChange={setBuffer}
                disabled={!manage}
              />
            </div>
            {pricingErr ? (
              <p role="alert" className="mt-1.5 text-[12px] font-semibold text-berry-deep">
                {pricingErr}
              </p>
            ) : (
              <p className="mt-1.5 text-[12px] leading-snug text-stone">
                Markup applies now; the buffer from the next sync.
              </p>
            )}
            {manage && (
              <button
                className="btn-ghost mt-2 h-9 w-full py-0 text-[13px] max-sm:h-10"
                onClick={savePricing}
                disabled={!pricingDirty}
              >
                Save pricing
              </button>
            )}
          </Stat>
        </div>
      )}

      {showSkips && skips && (
        <section className="card p-4 sm:p-5" aria-labelledby="skips-title">
          <div className="flex items-start gap-3">
            <h2 id="skips-title" className="flex-1 text-[15px] font-bold">
              Why {fmt(skips.total)} products weren’t imported
            </h2>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setShowSkips(false)}
              className="-m-1.5 grid h-9 w-9 shrink-0 place-items-center rounded-full text-stone transition hover:bg-canvas hover:text-ink"
            >
              <X size={16} />
            </button>
          </div>
          <div className="mt-3 space-y-4 text-[13px]">
            {skips.fx.length > 0 && (
              <div>
                <div className="font-semibold">
                  Priced in a local currency ({fmt(skips.fx.reduce((t, [, n]) => t + n, 0))}{' '}
                  products)
                </div>
                <p className="mt-0.5 max-w-prose leading-relaxed text-graphite">
                  {skips.legacy
                    ? 'This sync ran before currency conversion was deployed. Press Sync now to import them.'
                    : `WizzGift prices these in a local currency and the sync had no exchange rate for it${
                        data?.sync?.fxError ? ` (rate fetch: ${data.sync.fxError})` : ''
                      }.`}
                </p>
                <ul className="mt-2.5 flex flex-wrap gap-1.5">
                  {skips.fx.map(([cur, n]) => (
                    <li
                      key={cur}
                      className="inline-flex h-7 items-center gap-1.5 rounded-full bg-canvas px-2.5 text-[12px]"
                    >
                      <span className="font-mono font-semibold">{cur}</span>
                      <span className="tabular-nums text-graphite">{fmt(n)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {skips.other.length > 0 && (
              <ul className="divide-y divide-hairline rounded-[12px] border border-hairline">
                {skips.other.map(([reason, n]) => (
                  <li key={reason} className="flex items-baseline gap-3 px-3.5 py-2">
                    <span className="w-14 shrink-0 text-right font-bold tabular-nums">
                      {fmt(n)}
                    </span>
                    <span className="text-graphite">{reason}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {/* ---------- Catalog ---------- */}
      {!(error && !data) && (
        <section className="space-y-3" aria-label="Catalog">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <SearchInput
              value={q}
              onChange={setQ}
              placeholder="Search products"
              className="sm:max-w-sm sm:flex-1 [&_input]:h-10"
            />
            <input
              className="input h-10 py-0 text-[14px] uppercase placeholder:normal-case sm:w-44"
              placeholder="Country, e.g. US"
              aria-label="Country code"
              maxLength={2}
              value={country}
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
            />
            {data && (
              <span className="text-[13px] tabular-nums text-stone sm:ml-auto">
                {fmt(data.matched)} {data.matched === 1 ? 'product' : 'products'}
              </span>
            )}
          </div>

          <div className="card overflow-hidden">
            {isLoading ? (
              <SkeletonRows rows={5} />
            ) : !data || data.products.length === 0 ? (
              <EmptyState
                icon={Gift}
                title={data?.total ? 'No products match' : 'No products yet'}
                action={
                  filtered ? (
                    <button
                      className="btn-ghost h-10 py-0 text-[14px]"
                      onClick={() => {
                        setQ('');
                        setCountry('');
                      }}
                    >
                      Clear search
                    </button>
                  ) : !data?.total && manage ? (
                    <button
                      className="btn-primary h-10 py-0 text-[14px]"
                      onClick={sync}
                      disabled={syncing || running}
                    >
                      <RefreshCw size={15} aria-hidden="true" /> Sync now
                    </button>
                  ) : undefined
                }
              >
                {data?.total
                  ? 'Try another name, or a two-letter country code like US or GB.'
                  : 'Sync the catalog to pull cards and prices from WizzGift.'}
              </EmptyState>
            ) : (
              <>
                <ScrollX>
                  <table className="data-table min-w-[900px] table-fixed [&_td]:align-middle">
                    <colgroup>
                      <col />
                      <col className="w-[132px]" />
                      <col className="w-[124px]" />
                      <col className="w-[112px]" />
                      <col className="w-[108px]" />
                      <col className="w-[100px]" />
                      <col className="w-[80px]" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Where</th>
                        <th className="text-right">Amounts</th>
                        <th className="text-right" title="What one unit of the card costs us">
                          Cost per 1
                        </th>
                        <th title="Empty uses the default markup">Markup</th>
                        <th title="Empty uses the automatic order">Position</th>
                        <th className="text-center">Offered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.products.map((r) => {
                        const sku = r.skus[0];
                        return (
                          <tr key={r.id} className={r.available ? '' : 'text-stone'}>
                            <td>
                              <div
                                className={`flex min-w-0 items-center gap-3 ${r.available ? '' : 'opacity-60'}`}
                              >
                                <span className="grid h-[30px] w-10 shrink-0 place-items-center overflow-hidden rounded-[6px] border border-hairline bg-canvas">
                                  {r.imageUrl ? (
                                    <img
                                      src={r.imageUrl}
                                      alt=""
                                      className="h-full w-full object-cover"
                                    />
                                  ) : (
                                    <span className="font-bold text-graphite" aria-hidden="true">
                                      {r.name[0]}
                                    </span>
                                  )}
                                </span>
                                <div className="min-w-0">
                                  <div className="truncate font-semibold" title={r.name}>
                                    {r.name}
                                  </div>
                                  <div
                                    className={`truncate text-[12px] ${
                                      !r.available || r.needsFields
                                        ? 'font-semibold text-graphite'
                                        : 'text-stone'
                                    }`}
                                  >
                                    {!r.available
                                      ? 'No longer in the WizzGift catalog'
                                      : r.needsFields
                                        ? 'Not offered: asks for details we can’t read yet'
                                        : r.fields?.length
                                          ? `User enters: ${r.fields.join(', ')}`
                                          : r.id}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="truncate text-[13px]" title={r.countries.join(', ')}>
                              {r.countries.length === 0
                                ? 'Worldwide'
                                : r.countries.length > 3
                                  ? `${r.countries.slice(0, 3).join(', ')} +${r.countries.length - 3}`
                                  : r.countries.join(', ')}
                            </td>
                            <td className="truncate text-right text-[13px] tabular-nums">
                              {amounts(r)}
                            </td>
                            <td className="text-right text-[13px] tabular-nums">
                              {sku ? (
                                <>
                                  <div className="font-semibold">
                                    {usd(sku.minCostUsd / sku.min)}
                                  </div>
                                  {r.costCurrency && r.costCurrency !== 'USD' && (
                                    <div className="text-[11px] text-stone">
                                      {(sku.minCost / sku.min).toFixed(2)} {r.costCurrency}
                                    </div>
                                  )}
                                </>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td>
                              <span className="relative block">
                                <input
                                  className="input h-9 py-0 pl-2.5 pr-7 text-right text-[13px] tabular-nums"
                                  type="number"
                                  min={0}
                                  max={50}
                                  placeholder="—"
                                  aria-label={`Markup for ${r.name}, percent`}
                                  disabled={!manage}
                                  title={`Empty uses the default markup (${data.markupPercent}%)`}
                                  defaultValue={r.markupPercent ?? ''}
                                  onBlur={(e) => {
                                    const v = e.target.value === '' ? null : Number(e.target.value);
                                    if (v !== r.markupPercent)
                                      void patch(r.id, { markupPercent: v });
                                  }}
                                />
                                <span
                                  className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[12px] font-semibold text-stone"
                                  aria-hidden="true"
                                >
                                  %
                                </span>
                              </span>
                            </td>
                            <td>
                              <input
                                className="input h-9 px-2.5 py-0 text-right text-[13px] tabular-nums"
                                type="number"
                                min={0}
                                placeholder="Auto"
                                aria-label={`Position of ${r.name}`}
                                disabled={!manage}
                                title="Lower shows first. Empty uses the automatic order."
                                defaultValue={r.sort ?? ''}
                                onBlur={(e) => {
                                  const v = e.target.value === '' ? null : Number(e.target.value);
                                  if (v !== r.sort) void patch(r.id, { sort: v });
                                }}
                              />
                            </td>
                            <td className="text-center">
                              <button
                                role="switch"
                                aria-checked={r.enabled}
                                aria-label={`Offer ${r.name}`}
                                onClick={() => void patch(r.id, { enabled: !r.enabled })}
                                disabled={!manage}
                                className="group -my-1 inline-grid h-10 w-14 place-items-center rounded-full disabled:cursor-not-allowed"
                              >
                                <span
                                  className={`relative block h-6 w-11 rounded-full transition ${r.enabled ? 'bg-mint' : 'bg-ink/25 group-hover:bg-ink/35'}`}
                                >
                                  <span
                                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-[0_1px_2px_rgba(17,17,16,0.25)] transition-all ${r.enabled ? 'left-[22px]' : 'left-0.5'}`}
                                  />
                                </span>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </ScrollX>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-hairline px-4 py-3 text-[13px] text-graphite sm:px-5">
                  <p className="min-w-0 flex-1 basis-64 text-[12px] leading-snug text-stone">
                    Markup and position save when you leave the box. Leave them empty for the
                    default markup ({data.markupPercent}%) and the automatic order.
                  </p>
                  {data.matched > PAGE && (
                    <nav aria-label="Pagination" className="flex items-center gap-2">
                      <span className="mr-1 whitespace-nowrap tabular-nums">
                        {fmt(offset + 1)}–{fmt(Math.min(offset + PAGE, data.matched))} of{' '}
                        {fmt(data.matched)}
                      </span>
                      <button
                        className="btn-ghost h-10 px-3 py-0 text-[13px] sm:h-9"
                        disabled={offset === 0}
                        onClick={() => setOffset((o) => Math.max(0, o - PAGE))}
                      >
                        <ChevronLeft size={15} aria-hidden="true" /> Previous
                      </button>
                      <button
                        className="btn-ghost h-10 px-3 py-0 text-[13px] sm:h-9"
                        disabled={offset + PAGE >= data.matched}
                        onClick={() => setOffset((o) => o + PAGE)}
                      >
                        Next <ChevronRight size={15} aria-hidden="true" />
                      </button>
                    </nav>
                  )}
                </div>
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({
  label,
  loading,
  children,
}: {
  label: string;
  loading: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="card flex flex-col p-4 sm:p-5">
      <div className="mb-2 text-[13px] font-semibold text-stone">{label}</div>
      {loading ? (
        <div className="space-y-2" aria-hidden="true">
          <div className="h-8 w-2/3 animate-pulse rounded-[8px] bg-ink/[0.07]" />
          <div className="h-3 w-full animate-pulse rounded bg-ink/[0.05]" />
        </div>
      ) : (
        children
      )}
    </div>
  );
}

function PercentField({
  id,
  label,
  value,
  onChange,
  disabled = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1 block text-[12px] font-semibold text-graphite">
        {label}
      </label>
      <span className="relative block">
        <input
          id={id}
          className="input h-10 py-0 pl-3 pr-7 text-[15px] font-bold tabular-nums"
          type="number"
          min={0}
          max={50}
          step={0.5}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
        <span
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] font-semibold text-stone"
          aria-hidden="true"
        >
          %
        </span>
      </span>
    </div>
  );
}
